import React, { useState } from "react";
import ReactSelect from "react-select";
import { useModalKeyboardHandlers } from "../../useModalKeyboardHandlers";

const needleTypes = [
    { label: "Single Needle", value: "Single Needle" },
    { label: "Coaxial", value: "Coaxial" },
    { label: "Triaxial", value: "Triaxial" },
    // { label: "Needle Array", value: "Needle Array" },
    { label: "Needleless", value: "Needleless" },
];

// A needle size is its gauge, or its inner diameter in mm
const diameterUnits = [
    { label: "G", value: "G" },
    { label: "mm", value: "mm" },
];

const materials = [
    { label: "Stainless Steel", value: "Stainless Steel" },
    { label: "Titanium", value: "Titanium" },
    { label: "Plastic", value: "Plastic" },
    { label: "Aluminum", value: "Aluminum" },
    { label: "Carbon Fiber", value: "Carbon Fiber" },
];

const surfaceTypeOptions = [
    { label: "Disc", value: "Disc" },
    { label: "Wire", value: "Wire" },
    { label: "Plate", value: "Plate" },
];

export default function NeedleModal({ needleData, onClose, onSave }) {
    const [needle, setNeedle] = useState(() => {
        const defaultState = {
            needleType: "",
            diameter: "",
            diameterUnit: "G",
            innerDiameter: "",
            innerDiameterUnit: "G",
            outerDiameter: "",
            outerDiameterUnit: "G",
            middleDiameter: "",
            middleDiameterUnit: "G",
            numNeedles: "",
            needleSpacing: "",
            material: "",
        };

        if (!needleData) return defaultState;
        const { needleType = "", needleDefinition = {} } = needleData;
        return { ...defaultState, needleType, ...needleDefinition };
    });

    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setNeedle((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: null }));
    };

    const handleSelectChange = (name, selectedOption) => {
        setNeedle((prev) => ({ ...prev, [name]: selectedOption ? selectedOption.value : "" }));
        setErrors((prev) => ({ ...prev, [name]: null }));
    };

    const blockInvalidChars = (e) => {
        if (["e", "E", "-", "+"].includes(e.key)) {
            e.preventDefault();
        }
    };

    const validate = () => {
        const newErrors = {};
        const { needleType, surfaceType, innerNeedleType } = needle;

        if (!needleType) newErrors.needleType = "Needle type is required";

        const validatePositive = (field, label) => {
            const val = needle[field];
            if (val === "" || val === null || val === undefined) {
                newErrors[field] = `${label} is required`;
            } else if (Number(val) === 0) {
                newErrors[field] = `${label} cannot be zero`;
            } else if (Number(val) < 0) {
                newErrors[field] = `${label} must be a positive number`;
            }
        };

        switch (needleType) {
            case "Single Needle":
                validatePositive("diameter", "Diameter");
                break;
            case "Coaxial":
                validatePositive("innerDiameter", "Inner diameter");
                validatePositive("outerDiameter", "Outer diameter");
                break;
            case "Triaxial":
                validatePositive("innerDiameter", "Inner diameter");
                validatePositive("middleDiameter", "Middle diameter");
                validatePositive("outerDiameter", "Outer diameter");
                break;
            case "Needle Array":
                validatePositive("numNeedles", "Number of needles");
                validatePositive("needleSpacing", "Needle spacing");
                if (!innerNeedleType) {
                    newErrors.innerNeedleType = "Needle type in array is required";
                } else {
                    switch (innerNeedleType) {
                        case "Single Needle":
                            validatePositive("diameter", "Diameter");
                            break;
                        case "Coaxial":
                            validatePositive("innerDiameter", "Inner diameter");
                            validatePositive("outerDiameter", "Outer diameter");
                            break;
                        // Add Triaxial case if needed
                    }
                }
                break;
            case "Needleless":
                // Surface type removed; no required fields for Needleless
                break;
            default:
                break;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = (e) => {
        e.preventDefault();
        if (validate()) {
            const {
                needleType,
                diameter,
                diameterUnit,
                innerDiameter,
                outerDiameter,
                middleDiameter,
                numNeedles,
                needleSpacing,
                material,
            } = needle;

            let needleDefinition = {};

            switch (needleType) {
                case "Single Needle":
                    needleDefinition = { diameter: Number(diameter), diameterUnit, material };
                    break;
                case "Coaxial":
                    needleDefinition = { innerDiameter: Number(innerDiameter), innerDiameterUnit: needle.innerDiameterUnit || 'G', outerDiameter: Number(outerDiameter), outerDiameterUnit: needle.outerDiameterUnit || 'G', material };
                    break;
                case "Triaxial":
                    needleDefinition = { innerDiameter: Number(innerDiameter), innerDiameterUnit: needle.innerDiameterUnit || 'G', middleDiameter: Number(middleDiameter), middleDiameterUnit: needle.middleDiameterUnit || 'G', outerDiameter: Number(outerDiameter), outerDiameterUnit: needle.outerDiameterUnit || 'G', material };
                    break;
                case "Needle Array":
                    needleDefinition = {
                        numNeedles: Number(numNeedles),
                        needleSpacing: Number(needleSpacing),
                        needleType: needle.innerNeedleType,
                        diameter: needle.diameter ? Number(diameter) : undefined,
                        diameterUnit: needle.diameterUnit,
                        innerDiameter: needle.innerDiameter ? Number(innerDiameter) : undefined,
                        innerDiameterUnit: needle.innerDiameterUnit || 'G',
                        outerDiameter: needle.outerDiameter ? Number(outerDiameter) : undefined,
                        outerDiameterUnit: needle.outerDiameterUnit || 'G',
                        material,
                    };
                    break;
                case "Needleless":
                    // Surface type removed; just save material if present
                    needleDefinition = { material };
                    break;
                default:
                    break;
            }

            onSave({ needleType, needleDefinition });
            onClose();
        }
    };

    useModalKeyboardHandlers({
        onClose,
        onSave: (e) => handleSave(e),
        dependencies: [needle],
    });

    const renderInput = (label, name, placeholder, unit = null) => (
        <div className="col-md-6 mb-3">
            <label className="form-label fw-bold text-secondary">
                {label} <span className="text-danger">*</span>
            </label>
            <div className="input-group">
                <input
                    type="number"
                    step="any"
                    min="0"
                    name={name}
                    className={`form-control shadow-sm ${errors[name] ? "is-invalid" : ""}`}
                    value={needle[name]}
                    onChange={handleChange}
                    onKeyDown={blockInvalidChars}
                    placeholder={placeholder}
                />
                {unit && (
                    <select
                        className="form-select bg-light shadow-sm"
                        style={{ maxWidth: '100px' }}
                        value={needle[name + "Unit"] || unit[0].value}
                        onChange={(e) => setNeedle(prev => ({ ...prev, [name + "Unit"]: e.target.value }))}
                        aria-label={`${label} unit`}
                        title="G = needle gauge, mm = inner diameter"
                    >
                        {unit.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                    </select>
                )}
            </div>
            {errors[name] && <div className="text-danger small mt-1">{errors[name]}</div>}
        </div>
    );

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} role="dialog">
            <div className="modal-dialog modal-dialog-centered">
                <form className="modal-content shadow-lg border-0" onSubmit={handleSave}>
                    <div className="modal-header bg-light">
                        <h5 className="modal-title fw-bold text-dark">
                            <i className="bi bi-bullseye me-2 text-primary"></i>
                            Needle Details
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
                    </div>

                    <div className="modal-body p-4 bg-light-subtle">
                        <div className="card shadow-sm border-light mb-4">
                            <div className="card-body p-4">
                                <div className="mb-4">
                                    <label className="form-label fw-bold text-secondary">
                                        Needle Type <span className="text-danger">*</span>
                                    </label>
                                    <ReactSelect
                                        name="needleType"
                                        value={needleTypes.find(t => t.value === needle.needleType)}
                                        onChange={(opt) => handleSelectChange("needleType", opt)}
                                        options={needleTypes}
                                        placeholder="Select Type..."
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                minHeight: '38px',
                                                borderColor: '#dee2e6',
                                                boxShadow: 'none',
                                                '&:hover': { borderColor: '#adb5bd' }
                                            }),
                                            menuPortal: (base) => ({ ...base, zIndex: 99999 })
                                        }}
                                        menuPortalTarget={document.body}
                                    />
                                    {errors.needleType && <div className="text-danger small mt-1">{errors.needleType}</div>}
                                </div>

                                {needle.needleType && (
                                    <div className="row g-3 fade-in">
                                        {needle.needleType === "Single Needle" && (
                                            renderInput("Diameter", "diameter", "e.g. 18", diameterUnits)
                                        )}

                                        {needle.needleType === "Coaxial" && (
                                            <>
                                                {renderInput("Inner Diameter", "innerDiameter", "e.g. 0.5", diameterUnits)}
                                                {renderInput("Outer Diameter", "outerDiameter", "e.g. 1.0", diameterUnits)}
                                            </>
                                        )}

                                        {needle.needleType === "Triaxial" && (
                                            <>
                                                {renderInput("Inner Diameter", "innerDiameter", "e.g. 0.3", diameterUnits)}
                                                {renderInput("Middle Diameter", "middleDiameter", "e.g. 0.6", diameterUnits)}
                                                {renderInput("Outer Diameter", "outerDiameter", "e.g. 1.0", diameterUnits)}
                                            </>
                                        )}

                                        {["Single Needle", "Coaxial", "Triaxial"].includes(needle.needleType) && (
                                            <div className="col-12 small text-muted mt-0">
                                                Give the needle gauge (G), or its inner diameter in mm.
                                            </div>
                                        )}

                                        {needle.needleType === "Needleless" && null}

                                        <div className="col-12 mt-3">
                                            <label className="form-label fw-bold text-secondary">Material</label>
                                            <ReactSelect
                                                name="material"
                                                value={materials.find(m => m.value === needle.material)}
                                                onChange={(opt) => handleSelectChange("material", opt)}
                                                options={materials}
                                                placeholder="Select Material (Optional)..."
                                                styles={{
                                                    control: (base) => ({
                                                        ...base,
                                                        minHeight: '38px',
                                                        borderColor: '#dee2e6',
                                                        boxShadow: 'none',
                                                        '&:hover': { borderColor: '#adb5bd' }
                                                    }),
                                                    menuPortal: (base) => ({ ...base, zIndex: 99999 })
                                                }}
                                                menuPortalTarget={document.body}
                                                isClearable
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer bg-light border-top-0 py-3">
                        <button type="button" className="btn btn-outline-secondary px-4 rounded-pill fw-bold me-2" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary px-4 shadow-sm fw-bold rounded-pill">
                            <i className="bi bi-check2-circle me-1"></i>
                            Save Changes
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}